import React, { useState } from 'react';
import { Card } from './SubscriptionAppCard/Card';
import { Checkbox } from './Checkbox';
import { Info, Check, ChevronDown, Plus, X, Image as ImageIcon, ArrowLeft, ArrowRight } from 'lucide-react';
export function FlowPanel({
  onBack,
  onNext
}: {
  onBack?: () => void;
  onNext?: () => void;
}) {
  const [layoutMode, setLayoutMode] = useState<'single' | 'wizard'>('wizard');
  const [transition, setTransition] = useState('slide-horizontal');
  const [transitionSpeed, setTransitionSpeed] = useState('normal');
  const [customSpeed, setCustomSpeed] = useState('250');
  const [progressIndicator, setProgressIndicator] = useState('step-bar-labels');
  const [progressBarOptions, setProgressBarOptions] = useState({
    showStepLabels: true,
    showSummaryValues: true,
    showConnectingLines: false,
    showRunningTotal: false
  });
  const [progressBarCss, setProgressBarCss] = useState(`.widget-progress-bar { }\n.widget-progress-bar .step-dot { }\n.widget-progress-bar .step-dot.active { }\n.widget-progress-bar .step-line { }`);
  const [widgetTitle, setWidgetTitle] = useState('Ultimate Winery Plan');
  const [widgetDescription, setWidgetDescription] = useState('A wine club by Oak Valley Estate. Create your custom plan by selecting your preferences below.');
  const [valuePropositions, setValuePropositions] = useState(['20% added value in every box', '20% off all online purchases, always']);
  const [expandedSteps, setExpandedSteps] = useState<Set<number>>(new Set());
  const [stepContent, setStepContent] = useState<Record<number, {
    progressLabel: string;
    summaryFormat: string;
    headingOverride: string;
    description: string;
    css: string;
  }>>({
    1: {
      progressLabel: 'HOW MANY?',
      summaryFormat: '{pack_size}',
      headingOverride: 'How many bottles would you like?',
      description: 'Select your preferred pack size. Larger packs offer better value per bottle.',
      css: '/* .step--pack-size { } */'
    },
    2: {
      progressLabel: 'HOW OFTEN?',
      summaryFormat: '{frequency}',
      headingOverride: 'How often would you like your plan delivered?',
      description: 'Choose your preferred delivery schedule. You can skip, pause or change frequency at any time from your account.',
      css: '/* .step--frequency { } */'
    },
    3: {
      progressLabel: 'YOUR WINES',
      summaryFormat: '{count} selected',
      headingOverride: 'Choose your wines',
      description: "Browse our collection and pick your favourites. We'll pack them fresh for your next shipment.",
      css: '/* .step--products { } */'
    },
    4: {
      progressLabel: 'REVIEW',
      summaryFormat: '{total}',
      headingOverride: 'Review your order',
      description: 'Check your selections, pricing, and frequency before adding to cart.',
      css: '/* .step--review { } */'
    }
  });
  const toggleStepContent = (stepNumber: number) => {
    setExpandedSteps(prev => {
      const next = new Set(prev);
      if (next.has(stepNumber)) {
        next.delete(stepNumber);
      } else {
        next.add(stepNumber);
      }
      return next;
    });
  };
  const updateStepContent = (stepNumber: number, field: string, value: string) => {
    setStepContent(prev => ({
      ...prev,
      [stepNumber]: {
        ...prev[stepNumber],
        [field]: value
      }
    }));
  };
  const steps = [{
    number: 1,
    title: 'Choose Pack Size',
    description: 'Determines available frequencies, product pool, and pricing'
  }, {
    number: 2,
    title: 'Choose Frequency',
    description: 'Sets discount percentage shown on product cards and in the summary'
  }, {
    number: 3,
    title: 'Choose Products',
    description: "Product grid filtered to the pack size's collection. Curated boxes show a read-only display."
  }, {
    number: 4,
    title: 'Review & Add to Cart',
    description: 'Summary of selected wines, pricing, frequency, and checkout action'
  }];
  return <div className="flex flex-col gap-6 max-w-4xl mx-auto" data-id="element-1534">
      {/* Section 1: Layout Mode */}
      <Card padding="none" className="border border-[#E2E8F0]" data-id="element-1535">
        <div className="p-6" data-id="element-1536">
          <h2 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-1537">
            Layout Mode
          </h2>
          <p className="text-[13px] text-[#64748B] mb-6" data-id="element-1538">
            How the signup experience is presented to the customer on the
            storefront
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-1539">
            <div onClick={() => setLayoutMode('single')} className={`relative p-6 rounded-xl border-2 cursor-pointer transition-all duration-200 flex flex-col items-center text-center ${layoutMode === 'single' ? 'border-[#0EA5E9] bg-[#F0F9FF]' : 'border-[#E2E8F0] bg-white hover:border-[#CBD5E1]'}`} data-id="element-1540">
              {layoutMode === 'single' ? <div className="absolute top-4 right-4 w-6 h-6 rounded-full bg-[#0EA5E9] flex items-center justify-center" data-id="element-1541">
                  <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} data-id="element-1542" />
                </div> : <div className="absolute top-4 right-4 w-6 h-6 rounded-full border-2 border-[#CBD5E1]" data-id="element-1543" />}
              <div className="w-full aspect-[16/9] bg-[#F1F5F9] rounded-lg mb-6 p-4 flex flex-col items-center justify-center gap-2" data-id="element-1544">
                <div className="w-1/2 h-3 bg-[#CBD5E1] rounded-full mb-2" data-id="element-1545" />
                <div className="w-3/4 h-2 bg-[#E2E8F0] rounded-full" data-id="element-1546" />
                <div className="w-3/4 h-2 bg-[#E2E8F0] rounded-full" data-id="element-1547" />
                <div className="w-3/4 h-2 bg-[#E2E8F0] rounded-full" data-id="element-1548" />
                <div className="w-3/4 h-2 bg-[#E2E8F0] rounded-full" data-id="element-1549" />
              </div>
              <h3 className="text-[16px] font-semibold text-[#0F172A] mb-2" data-id="element-1550">
                Single Page
              </h3>
              <p className="text-[13px] text-[#64748B] leading-relaxed" data-id="element-1551">
                All steps visible in one scrollable page. Fast for simple boxes.
              </p>
            </div>
            <div onClick={() => setLayoutMode('wizard')} className={`relative p-6 rounded-xl border-2 cursor-pointer transition-all duration-200 flex flex-col items-center text-center ${layoutMode === 'wizard' ? 'border-[#0EA5E9] bg-[#E0F2FE]' : 'border-[#E2E8F0] bg-white hover:border-[#CBD5E1]'}`} data-id="element-1552">
              {layoutMode === 'wizard' ? <div className="absolute top-4 right-4 w-6 h-6 rounded-full bg-[#0EA5E9] flex items-center justify-center" data-id="element-1553">
                  <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} data-id="element-1554" />
                </div> : <div className="absolute top-4 right-4 w-6 h-6 rounded-full border-2 border-[#CBD5E1]" data-id="element-1555" />}
              <div className="w-full aspect-[16/9] bg-[#E0F2FE] rounded-lg mb-6 flex items-end justify-center gap-2 px-8 pb-4" data-id="element-1556">
                <div className="w-4 h-8 bg-[#BFDBFE] rounded-t-sm" data-id="element-1557" />
                <div className="w-4 h-12 bg-[#BFDBFE] rounded-t-sm" data-id="element-1558" />
                <div className="w-4 h-16 bg-[#3B82F6] rounded-t-sm" data-id="element-1559" />
                <div className="w-4 h-10 bg-[#BFDBFE] rounded-t-sm" data-id="element-1560" />
              </div>
              <h3 className="text-[16px] font-semibold text-[#0F172A] mb-2" data-id="element-1561">
                Multi-Step Wizard
              </h3>
              <p className="text-[13px] text-[#64748B] leading-relaxed" data-id="element-1562">
                Guided step-by-step flow with progress bar. Focused experience,
                one decision at a time.
              </p>
            </div>
          </div>
          <div className="bg-[#F0F9FF] border border-[#E0F2FE] rounded-lg p-4 flex gap-3" data-id="element-1563">
            <Info className="w-5 h-5 text-[#0EA5E9] flex-shrink-0 mt-0.5" data-id="element-1564" />
            <p className="text-[13px] text-[#475569] leading-relaxed" data-id="element-1565">
              <span className="font-semibold text-[#0F172A]" data-id="element-1566">Multi-step</span>{' '}
              shows a progress bar and Next/Back buttons. Each block becomes its
              own step.{' '}
              <span className="font-semibold text-[#0F172A]" data-id="element-1567">Single page</span>{' '}
              stacks all blocks vertically with anchor navigation. Both use the
              same Theme App Extension blocks — agencies can override styling
              either way.
            </p>
          </div>
          {layoutMode === 'wizard' && <div className="mt-6 border border-[#E2E8F0] rounded-xl p-6" data-id="element-1568">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1569">
                <div data-id="element-1570">
                  <label className="block text-[14px] font-semibold text-[#0F172A] mb-3" data-id="element-1571">
                    Transition
                  </label>
                  <div className="relative" data-id="element-1572">
                    <select value={transition} onChange={e => setTransition(e.target.value)} className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 appearance-none cursor-pointer" data-id="element-1573">
                      <option value="slide-horizontal" data-id="element-1574">Slide horizontal</option>
                      <option value="slide-vertical" data-id="element-1575">Slide vertical</option>
                      <option value="fade" data-id="element-1576">Fade</option>
                      <option value="instant" data-id="element-1577">Instant (no animation)</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B] pointer-events-none" data-id="element-1578" />
                  </div>
                  <p className="mt-1.5 text-[12px] text-[#94A3B8]" data-id="element-1579">
                    How the widget animates between steps
                  </p>
                </div>
                <div data-id="element-1580">
                  <label className="block text-[14px] font-semibold text-[#0F172A] mb-3" data-id="element-1581">
                    Transition Speed
                  </label>
                  <div className="relative" data-id="element-1582">
                    <select value={transitionSpeed} onChange={e => setTransitionSpeed(e.target.value)} className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 appearance-none cursor-pointer" data-id="element-1583">
                      <option value="slow" data-id="element-1584">Slow (500ms)</option>
                      <option value="normal" data-id="element-1585">Normal (300ms)</option>
                      <option value="fast" data-id="element-1586">Fast (150ms)</option>
                      <option value="custom" data-id="element-1587">Custom</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B] pointer-events-none" data-id="element-1588" />
                  </div>
                  {transitionSpeed === 'custom' && <div className="mt-2 flex rounded-[6px] overflow-hidden border border-[#CBD5E1] focus-within:border-[#0EA5E9] focus-within:ring-[3px] focus-within:ring-[#0EA5E9]/20" data-id="element-1589">
                      <input type="number" value={customSpeed} onChange={e => setCustomSpeed(e.target.value)} min="0" max="5000" step="50" className="flex-1 px-3 py-2 text-[14px] text-[#0F172A] focus:outline-none w-16 text-center font-medium" data-id="element-1590" />
                      <div className="bg-[#F8FAFC] px-4 py-2 text-[14px] text-[#64748B] border-l border-[#CBD5E1] flex items-center" data-id="element-1591">
                        ms
                      </div>
                    </div>}
                  <p className="mt-1.5 text-[12px] text-[#94A3B8]" data-id="element-1592">
                    Animation duration. Ignored when transition is set to
                    Instant.
                  </p>
                </div>
                <div data-id="element-1593">
                  <label className="block text-[14px] font-semibold text-[#0F172A] mb-3" data-id="element-1594">
                    Progress Indicator
                  </label>
                  <div className="relative" data-id="element-1595">
                    <select value={progressIndicator} onChange={e => setProgressIndicator(e.target.value)} className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 appearance-none cursor-pointer" data-id="element-1596">
                      <option value="step-bar-labels" data-id="element-1597">
                        Step bar with labels
                      </option>
                      <option value="step-bar-no-labels" data-id="element-1598">
                        Step bar without labels
                      </option>
                      <option value="dots" data-id="element-1599">Dots</option>
                      <option value="fraction" data-id="element-1600">Fraction (1 of 4)</option>
                      <option value="none" data-id="element-1601">None</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B] pointer-events-none" data-id="element-1602" />
                  </div>
                  <p className="mt-1.5 text-[12px] text-[#94A3B8]" data-id="element-1603">
                    How the customer sees their progress through the flow
                  </p>
                </div>
              </div>
              <div className="mt-6 border-t border-dashed border-[#E2E8F0] pt-6" data-id="element-1604">
                <label className="block text-[12px] font-semibold text-[#0EA5E9] uppercase tracking-wider mb-4" data-id="element-1605">
                  Progress Bar Options
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1606">
                  {[{
                key: 'showStepLabels',
                label: 'Show step labels',
                desc: 'Display labels like "How many?", "How often?" above the progress dots'
              }, {
                key: 'showSummaryValues',
                label: 'Show step summary values',
                desc: 'Display current selection under each label (e.g. "Three Wines", "$99/mo")'
              }, {
                key: 'showConnectingLines',
                label: 'Show connecting lines',
                desc: 'Lines connecting the step dots to show progression'
              }, {
                key: 'showRunningTotal',
                label: 'Show running total',
                desc: 'Display a running order total in the last progress step'
              }].map(({
                key,
                label,
                desc
              }) => <div key={key} className={`rounded-lg p-4 flex gap-3 border ${progressBarOptions[key as keyof typeof progressBarOptions] ? 'border-[#0EA5E9] bg-[#F0F9FF]' : 'border-[#E2E8F0] bg-white'}`} data-id="element-1607">
                      <div className="pt-0.5" data-id="element-1608">
                        <Checkbox checked={progressBarOptions[key as keyof typeof progressBarOptions]} onChange={checked => setProgressBarOptions(prev => ({
                    ...prev,
                    [key]: checked
                  }))} data-id="element-1609" />
                      </div>
                      <div data-id="element-1610">
                        <div className="text-[14px] font-semibold text-[#0F172A]" data-id="element-1611">
                          {label}
                        </div>
                        <div className="text-[13px] text-[#64748B]" data-id="element-1612">{desc}</div>
                      </div>
                    </div>)}
                </div>
                <div className="mt-6" data-id="element-1613">
                  <label className="block text-[14px] font-semibold text-[#0F172A] mb-2" data-id="element-1614">
                    Progress Bar CSS
                  </label>
                  <textarea value={progressBarCss} onChange={e => setProgressBarCss(e.target.value)} className="w-full h-[120px] px-4 py-3 bg-[#0F172A] border border-[#334155] rounded-[6px] text-[13px] text-[#94A3B8] font-mono focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 resize-y leading-relaxed" data-id="element-1615" />
                  <p className="mt-1.5 text-[12px] text-[#94A3B8]" data-id="element-1616">
                    CSS scoped to{' '}
                    <code className="font-mono text-[#64748B]" data-id="element-1617">
                      .widget-progress-bar
                    </code>
                    . Override dot size, colours, icons, connecting line styles.
                    Per-step labels are configured in the Signup Flow card
                    below.
                  </p>
                </div>
              </div>
            </div>}
        </div>
      </Card>

      {/* Section 2: Widget Header */}
      <Card padding="none" className="border border-[#E2E8F0]" data-id="element-1618">
        <div className="p-6" data-id="element-1619">
          <h2 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-1620">
            Widget Header
          </h2>
          <p className="text-[13px] text-[#64748B] mb-6" data-id="element-1621">
            Static content shown above the signup steps — visible throughout the
            entire flow
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-1622">
            <div data-id="element-1623">
              <label className="block text-[14px] font-semibold text-[#0F172A] mb-2" data-id="element-1624">
                Widget Title
              </label>
              <input type="text" value={widgetTitle} onChange={e => setWidgetTitle(e.target.value)} placeholder="e.g. Ultimate Winery Plan" className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20" data-id="element-1625" />
              <p className="mt-1.5 text-[12px] text-[#94A3B8]" data-id="element-1626">
                Main heading displayed above the progress bar. Leave blank to
                hide.
              </p>
            </div>
            <div data-id="element-1627">
              <label className="block text-[14px] font-semibold text-[#0F172A] mb-2" data-id="element-1628">
                Header Image
              </label>
              <div className="flex items-center gap-4" data-id="element-1629">
                <div className="w-14 h-14 rounded-lg border-2 border-dashed border-[#CBD5E1] flex items-center justify-center text-[#94A3B8] flex-shrink-0 cursor-pointer hover:border-[#0EA5E9] hover:text-[#0EA5E9] transition-colors" data-id="element-1630">
                  <ImageIcon className="w-6 h-6" data-id="element-1631" />
                </div>
                <div data-id="element-1632">
                  <p className="text-[13px] text-[#475569]" data-id="element-1633">
                    <span className="font-bold text-[#0F172A]" data-id="element-1634">
                      Upload image
                    </span>{' '}
                    or drag and drop
                  </p>
                  <p className="text-[12px] text-[#94A3B8] mt-0.5" data-id="element-1635">
                    Hero banner displayed above the title.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="mb-6" data-id="element-1636">
            <label className="block text-[14px] font-semibold text-[#0F172A] mb-2" data-id="element-1637">
              Widget Description
            </label>
            <textarea value={widgetDescription} onChange={e => setWidgetDescription(e.target.value)} placeholder="Describe your wine club to customers..." className="w-full h-[100px] px-3 py-2 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 resize-y" data-id="element-1638" />
            <p className="mt-1.5 text-[12px] text-[#94A3B8]" data-id="element-1639">
              Supporting copy shown below the widget title
            </p>
          </div>
          <div className="mb-6" data-id="element-1640">
            <label className="block text-[14px] font-semibold text-[#0F172A] mb-1" data-id="element-1641">
              Value Propositions
            </label>
            <p className="text-[12px] text-[#94A3B8] mb-3" data-id="element-1642">
              Short benefit statements shown as pills between the description
              and the first step
            </p>
            <div className="space-y-3" data-id="element-1643">
              {valuePropositions.map((prop, index) => <div key={index} className="flex items-center gap-3 border border-[#E2E8F0] rounded-lg bg-white" data-id="element-1644">
                  <input type="text" value={prop} onChange={e => {
                const updated = [...valuePropositions];
                updated[index] = e.target.value;
                setValuePropositions(updated);
              }} className="flex-1 h-[44px] px-4 text-[14px] text-[#0F172A] bg-transparent focus:outline-none" data-id="element-1645" />
                  <button onClick={() => setValuePropositions(prev => prev.filter((_, i) => i !== index))} className="p-2 mr-2 text-[#94A3B8] hover:text-[#EF4444] hover:bg-[#FEF2F2] rounded transition-colors" data-id="element-1646">
                    <X className="w-4 h-4" data-id="element-1647" />
                  </button>
                </div>)}
              <button onClick={() => setValuePropositions(prev => [...prev, ''])} className="w-full py-3 border-2 border-dashed border-[#CBD5E1] rounded-lg flex items-center justify-center gap-2 text-[13px] font-medium text-[#0EA5E9] hover:border-[#0EA5E9] hover:bg-[#F0F9FF] transition-colors" data-id="element-1648">
                <Plus className="w-4 h-4" data-id="element-1649" />
                Add Value Prop
              </button>
            </div>
          </div>
          <div className="border border-[#E2E8F0] rounded-xl bg-[#F8FAFC] p-8 text-center" data-id="element-1650">
            {widgetTitle && <h3 className="text-[20px] font-bold text-[#0F172A] mb-2" data-id="element-1651">
                {widgetTitle}
              </h3>}
            {widgetDescription && <p className="text-[14px] text-[#94A3B8] mb-4 max-w-xl mx-auto" data-id="element-1652">
                {widgetDescription}
              </p>}
            {valuePropositions.filter(v => v.trim()).length > 0 && <div className="flex items-center justify-center gap-3 flex-wrap" data-id="element-1653">
                {valuePropositions.filter(v => v.trim()).map((prop, index) => <span key={index} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-[#E2E8F0] bg-white text-[13px] text-[#475569]" data-id="element-1654">
                      <Check className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-1655" />
                      {prop}
                    </span>)}
              </div>}
          </div>
        </div>
      </Card>

      {/* Section 3: Signup Flow */}
      <Card padding="none" className="border border-[#E2E8F0]" data-id="element-1656">
        <div className="p-6" data-id="element-1657">
          <h2 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-1658">
            Signup Flow
          </h2>
          <p className="text-[13px] text-[#64748B] mb-8" data-id="element-1659">
            The core steps run in a fixed order driven by the data model.
            Optional blocks can be toggled on and positioned between steps.
          </p>
          <div className="space-y-8 relative" data-id="element-1660">
            <div className="absolute left-[15px] top-4 bottom-4 w-0.5 bg-[#E2E8F0]" data-id="element-1661" />
            {steps.map(step => {
            const isExpanded = expandedSteps.has(step.number);
            const content = stepContent[step.number];
            return <div key={step.number} className="relative" data-id="element-1662">
                  <div className="flex gap-4" data-id="element-1663">
                    <div className="relative z-10 w-8 h-8 rounded-full bg-[#E0F2FE] flex items-center justify-center flex-shrink-0 text-[#0EA5E9] font-bold text-[14px]" data-id="element-1664">
                      {step.number}
                    </div>
                    <div className="flex-1 pt-1" data-id="element-1665">
                      <div className="flex items-start justify-between gap-4" data-id="element-1666">
                        <div data-id="element-1667">
                          <div className="flex items-center gap-2 mb-1" data-id="element-1668">
                            <h3 className="text-[14px] font-semibold text-[#0F172A]" data-id="element-1669">
                              {step.title}
                            </h3>
                            <button onClick={() => toggleStepContent(step.number)} className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold transition-colors ${isExpanded ? 'bg-[#E0F2FE] text-[#0284C7]' : 'bg-[#E0F2FE] text-[#0EA5E9] hover:bg-[#BFDBFE]'}`} data-id="element-1670">
                              <Plus className="w-3 h-3" data-id="element-1671" />
                              Content
                            </button>
                          </div>
                          <p className="text-[13px] text-[#94A3B8]" data-id="element-1672">
                            {step.description}
                          </p>
                        </div>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded text-[11px] font-bold bg-[#F1F5F9] text-[#64748B] uppercase tracking-wider border border-[#E2E8F0]" data-id="element-1673">
                          Fixed
                        </span>
                      </div>
                      {isExpanded && content && <div className="mt-4 border border-[#E2E8F0] rounded-xl bg-[#F8FAFC] p-5 space-y-5" data-id="element-1674">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-1675">
                            <div data-id="element-1676">
                              <label className="block text-[13px] font-semibold text-[#0F172A] mb-1.5" data-id="element-1677">
                                Progress Label
                              </label>
                              <input type="text" value={content.progressLabel} onChange={e => updateStepContent(step.number, 'progressLabel', e.target.value)} className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20" data-id="element-1678" />
                              <p className="mt-1.5 text-[12px] text-[#94A3B8]" data-id="element-1679">
                                Text shown above the progress dot for this step
                              </p>
                            </div>
                            <div data-id="element-1680">
                              <label className="block text-[13px] font-semibold text-[#0F172A] mb-1.5" data-id="element-1681">
                                Progress Summary Format
                              </label>
                              <input type="text" value={content.summaryFormat} onChange={e => updateStepContent(step.number, 'summaryFormat', e.target.value)} className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20" data-id="element-1682" />
                              <p className="mt-1.5 text-[12px] text-[#94A3B8]" data-id="element-1683">
                                Dynamic value shown below the label.
                              </p>
                            </div>
                          </div>
                          <div data-id="element-1684">
                            <label className="block text-[13px] font-semibold text-[#0F172A] mb-1.5" data-id="element-1685">
                              Step Heading Override
                            </label>
                            <input type="text" value={content.headingOverride} onChange={e => updateStepContent(step.number, 'headingOverride', e.target.value)} className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20" data-id="element-1686" />
                            <p className="mt-1.5 text-[12px] text-[#94A3B8]" data-id="element-1687">
                              Replaces the default "{step.title}" heading on
                              this step
                            </p>
                          </div>
                          <div data-id="element-1688">
                            <label className="block text-[13px] font-semibold text-[#0F172A] mb-1.5" data-id="element-1689">
                              Step Description
                            </label>
                            <textarea value={content.description} onChange={e => updateStepContent(step.number, 'description', e.target.value)} className="w-full h-[80px] px-3 py-2 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 resize-y" data-id="element-1690" />
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-1691">
                            <div data-id="element-1692">
                              <label className="block text-[13px] font-semibold text-[#0F172A] mb-1.5" data-id="element-1693">
                                Step Image
                              </label>
                              <div className="flex items-center gap-3" data-id="element-1694">
                                <div className="w-12 h-12 rounded-lg border-2 border-dashed border-[#CBD5E1] flex items-center justify-center text-[#94A3B8] flex-shrink-0 cursor-pointer hover:border-[#0EA5E9] hover:text-[#0EA5E9] transition-colors" data-id="element-1695">
                                  <ImageIcon className="w-5 h-5" data-id="element-1696" />
                                </div>
                                <div data-id="element-1697">
                                  <p className="text-[13px] font-semibold text-[#0F172A]" data-id="element-1698">
                                    Upload image
                                  </p>
                                  <p className="text-[12px] text-[#94A3B8]" data-id="element-1699">
                                    Banner above or beside the step
                                  </p>
                                </div>
                              </div>
                            </div>
                            <div data-id="element-1700">
                              <label className="block text-[13px] font-semibold text-[#0F172A] mb-1.5" data-id="element-1701">
                                Step CSS
                              </label>
                              <textarea value={content.css} onChange={e => updateStepContent(step.number, 'css', e.target.value)} className="w-full h-[60px] px-4 py-3 bg-[#0F172A] border border-[#334155] rounded-[6px] text-[13px] text-[#94A3B8] font-mono focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 resize-y" data-id="element-1702" />
                            </div>
                          </div>
                        </div>}
                    </div>
                  </div>
                </div>;
          })}
          </div>
          <div className="mt-8 bg-[#F0F9FF] border border-[#E0F2FE] rounded-lg p-4 flex gap-3" data-id="element-1703">
            <Info className="w-5 h-5 text-[#0EA5E9] flex-shrink-0 mt-0.5" data-id="element-1704" />
            <p className="text-[13px] text-[#475569] leading-relaxed" data-id="element-1705">
              This order can't be changed because each step depends on the
              previous one. Pack size determines which frequencies and products
              are available, and frequency sets the discount shown during
              product selection.
            </p>
          </div>
        </div>
      </Card>

      {/* Navigation Footer */}
      <div className="flex items-center justify-between pt-2 pb-4" data-id="element-1706">
        <button onClick={onBack} className="flex items-center gap-2 px-4 py-2 text-[14px] font-medium text-[#475569] hover:text-[#0F172A] border border-[#E2E8F0] bg-white rounded-[6px] hover:border-[#CBD5E1] transition-colors" data-id="element-1707">
          <ArrowLeft className="w-4 h-4" data-id="element-1708" />
          Membership
        </button>
        <button onClick={onNext} className="flex items-center gap-2 px-5 py-2 text-[14px] font-medium text-white bg-[#0EA5E9] hover:bg-[#0284C7] rounded-[6px] transition-colors" data-id="element-1709">
          Next: Theming
          <ArrowRight className="w-4 h-4" data-id="element-1710" />
        </button>
      </div>
    </div>;
}